import { useCallback, useEffect, useRef, useState } from 'react'
import { createRoot } from 'react-dom/client'
import type { SessionContext } from '@esign/contracts'
import { App } from './App.js'
import { middlewareApi } from './middleware-api.js'
import { sessionIdFromLocation, stepFromLocation, webviewEntryPath, webviewTokenFromLocation } from './route.js'
import { DEMO_SESSION_CONTEXT, DEMO_SESSION_ID, demoSessionContext } from './demo-session.js'
import {
  applyPartnerBranding,
  brandingFromTestSession,
  loadPartnerBranding,
  persistPartnerBranding,
  normalizeHexColor,
} from './partner-branding.js'
import { applyTheme } from './theme.js'
import '@esign/ui/ui.css'
import '@esign/steps/steps.css'
import './app.css'
import './startup.css'

function persistWebviewToken(token: string, sessionId?: string | null): void {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem('dev_webview_token', token)
    if (sessionId) sessionStorage.setItem('dev_session_id', sessionId)
  }
  window.__ESIGN_CONFIG__ = {
    ...window.__ESIGN_CONFIG__,
    MIDDLEWARE_WEBVIEW_TOKEN: token,
  }
}

function stripTokenFromAddressBar(): void {
  const url = new URL(window.location.href)
  let changed = false
  for (const key of ['token', 'webview_token', 'bearer'] as const) {
    if (url.searchParams.has(key)) {
      url.searchParams.delete(key)
      changed = true
    }
  }
  if (changed) history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`)
}

function Bootstrap() {
  const urlParams = new URLSearchParams(window.location.search)
  const tokenFromUrl = webviewTokenFromLocation(window.location)
  const urlUserId = urlParams.get('userId') || urlParams.get('user_id')
  const urlEmail = urlParams.get('email') || urlParams.get('user_email')
  const urlFullName = urlParams.get('fullName') || urlParams.get('full_name') || urlParams.get('name')
  const urlPhone = urlParams.get('phone') || urlParams.get('phone_number') || urlParams.get('sdt') || urlParams.get('mobile')
  const urlAddress = urlParams.get('address') || urlParams.get('permanent_address') || urlParams.get('dia_chi')
  const urlDob = urlParams.get('date_of_birth') || urlParams.get('dob') || urlParams.get('birthday')
  const urlIdentityCard = urlParams.get('identity_card') || urlParams.get('id_no') || urlParams.get('id_number')
  const urlBankName = urlParams.get('bank_name') || urlParams.get('bank')
  const urlAccountNumber = urlParams.get('account_number') || urlParams.get('bank_account') || urlParams.get('bank_account_number')
  const urlAccountName = urlParams.get('account_name') || urlParams.get('bank_account_name')
  const urlBankBranch = urlParams.get('bank_branch') || urlParams.get('branch') || urlParams.get('bank_branch_name')

  const urlPrimary = normalizeHexColor(
    urlParams.get('primary') ||
      urlParams.get('brand_color') ||
      urlParams.get('theme_color') ||
      urlParams.get('theme'),
  )
  const urlSecondary = normalizeHexColor(urlParams.get('secondary') || urlParams.get('secondary_color'))
  const urlOnPrimary = normalizeHexColor(urlParams.get('on_primary') || urlParams.get('onPrimary'))
  const urlSessionId = sessionIdFromLocation(window.location)

  const [context, setContext] = useState<SessionContext | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [attempt, setAttempt] = useState(0)

  const retry = useCallback(() => {
    hasStartedRef.current = false
    setAttempt((value) => value + 1)
  }, [])

  const handleExit = useCallback(() => {
    try {
      if (typeof window !== 'undefined' && (window as any).ReactNativeWebView) {
        (window as any).ReactNativeWebView.postMessage(
          JSON.stringify({
            type: 'esign:exit',
            status: 'cancelled',
            session_id: urlSessionId || undefined,
          }),
        )
      } else {
        window.history.back()
      }
    } catch {
      // ignore
    }
  }, [urlSessionId])

  const hasStartedRef = useRef(false)

  useEffect(() => {
    if (hasStartedRef.current) return
    hasStartedRef.current = true

    setError(null)
    setContext(null)

    const pathname = window.location.pathname
    const isRootOrInfo = (pathname === '/' || pathname === '' || pathname === '/info') && !urlSessionId && !tokenFromUrl

    // 1. Khi mở hoặc refresh trang chủ / không kèm token: Gọi test-session lấy session_id và token mới (cho dev/test)
    if (isRootOrInfo) {
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem('dev_session_id')
        sessionStorage.removeItem('dev_webview_token')
        sessionStorage.removeItem('dev_ekyc_access_token')
        sessionStorage.removeItem('dev_ekyc_sdk_url')
        sessionStorage.removeItem('dev_ekyc_sdk_token')
        sessionStorage.removeItem('dev_ekyc_sdk_token_name')
        sessionStorage.removeItem('dev_ekyc_sdk_resource_id')
      }

      const randomUserNumber = Math.floor(Math.random() * 900000) + 100000
      const effectiveUserId = urlUserId || `user_${randomUserNumber}`
      const effectiveEmail = urlEmail || (urlUserId ? `${urlUserId}@oneat.vn` : `user${randomUserNumber}@test.com`)
      const effectiveFullName = urlFullName || 'Nguyễn Văn A'

      void middlewareApi
        .createTestSession({
          user_id: effectiveUserId,
          partner_code: 'AT',
          identity_info: {
            email: effectiveEmail,
            full_name: effectiveFullName,
            ...(urlPhone ? { phone: urlPhone, phone_number: urlPhone } : {}),
            ...(urlAddress ? { address: urlAddress, permanent_address: urlAddress } : {}),
            ...(urlDob ? { date_of_birth: urlDob, dob: urlDob } : {}),
            ...(urlIdentityCard ? { identity_card: urlIdentityCard, id_no: urlIdentityCard, id_number: urlIdentityCard } : {}),
          },
          ...((urlBankName || urlAccountNumber) ? {
            payment_info: {
              bank_name: urlBankName || '',
              bank: urlBankName || '',
              account_number: urlAccountNumber || '',
              account_name: urlAccountName || effectiveFullName,
              branch: urlBankBranch || '',
              bank_branch: urlBankBranch || '',
            }
          } : {}),
        })
        .then((sessionRes: any) => {
          const testData = sessionRes?.data
          const freshToken = testData?.webview_token
          const freshSessionId = testData?.session_id || DEMO_SESSION_ID
          const branding = brandingFromTestSession(testData)
          persistPartnerBranding(freshSessionId, branding)

          if (freshToken) persistWebviewToken(freshToken, freshSessionId)

          if (typeof sessionStorage !== 'undefined') {
            const initialIdentity = {
              email: effectiveEmail,
              full_name: effectiveFullName,
              user_id: effectiveUserId,
              ...(urlPhone ? { phone: urlPhone, phone_number: urlPhone } : {}),
              ...(urlAddress ? { address: urlAddress, permanent_address: urlAddress } : {}),
              ...(urlDob ? { date_of_birth: urlDob, dob: urlDob } : {}),
              ...(urlIdentityCard ? { identity_card: urlIdentityCard, id_no: urlIdentityCard, id_number: urlIdentityCard, tax_code: urlIdentityCard } : {}),
              ...(urlBankName ? { bank_name: urlBankName, bank: urlBankName } : {}),
              ...(urlAccountNumber ? { account_number: urlAccountNumber, bank_account: urlAccountNumber } : {}),
              ...(urlAccountName ? { account_name: urlAccountName, bank_account_name: urlAccountName } : {}),
              ...(urlBankBranch ? { bank_branch: urlBankBranch, branch: urlBankBranch } : {}),
            }
            sessionStorage.setItem('dev_identity_data', JSON.stringify(initialIdentity))
            sessionStorage.setItem(`dev_identity_data_${freshSessionId}`, JSON.stringify(initialIdentity))
          }

          const demoContext = demoSessionContext(webviewEntryPath(freshSessionId), freshSessionId)
          const branded = applyPartnerBranding(demoContext, branding, {
            ...(urlPrimary ? { brand_color: urlPrimary } : {}),
            ...(urlSecondary ? { secondary_color: urlSecondary } : {}),
            ...(urlOnPrimary ? { on_primary: urlOnPrimary } : {}),
          })
          applyTheme(branded.theme)

          const identity = testData?.identity_info
          setContext({
            ...branded,
            session: {
              ...branded.session,
              id: freshSessionId,
            },
            subject: {
              ...branded.subject,
              full_name: identity?.full_name || effectiveFullName,
              email: identity?.email || effectiveEmail,
              phone: identity?.phone || urlPhone || branded.subject.phone,
              address: identity?.address || urlAddress || branded.subject.address,
              date_of_birth: identity?.date_of_birth || urlDob || branded.subject.date_of_birth,
              id_no: identity?.identity_card || urlIdentityCard || branded.subject.id_no,
            },
          })

          const nextUrl = new URL(window.location.href)
          nextUrl.pathname = webviewEntryPath(freshSessionId)
          history.replaceState(null, '', `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`)
        })
        .catch((err: unknown) => {
          console.warn('createTestSession error:', err)
          const fallbackContext = demoSessionContext(pathname, DEMO_SESSION_ID)
          applyTheme(fallbackContext.theme)
          setContext(fallbackContext)
        })

      return
    }

    // 2. Partner mở webview_url (/webview/:sessionId) hoặc deep-link /s/:sessionId/:step
    const savedSessionId = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('dev_session_id') : null
    const savedToken = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('dev_webview_token') : null

    // Chỉ tái sử dụng token đã lưu nếu người dùng F5/refresh đúng session_id đó
    const isRefreshingSameSession = Boolean(savedSessionId && urlSessionId && savedSessionId === urlSessionId)
    const activeToken = tokenFromUrl || (isRefreshingSameSession ? savedToken : null)
    const activeSessionId = urlSessionId || (tokenFromUrl ? null : savedSessionId)

    // Chặn nếu session_id là placeholder mẫu như {session_id} hoặc rỗng
    const isPlaceholderSession = !activeSessionId || activeSessionId.includes('{') || activeSessionId.includes('}')

    if (isPlaceholderSession) {
      setError('Đường dẫn phiên làm việc không hợp lệ (mã session_id không đúng định dạng). Vui lòng kiểm tra lại liên kết.')
      return
    }

    if (!activeToken) {
      setError('Thiếu token xác thực phiên Webview. Vui lòng mở lại từ ứng dụng ngân hàng hoặc đính kèm token trên URL.')
      return
    }

    persistWebviewToken(activeToken, activeSessionId)
    if (tokenFromUrl) {
      stripTokenFromAddressBar()
    }

    // Gọi API /api/v1/webview/init để xác thực token và nhận thông tin phiên / đối tác
    void middlewareApi
      .initWebview(activeSessionId, { session_id: activeSessionId }, { token: activeToken })
      .then((res: any) => {
        const initData = res?.data
        const effectiveSessionId = initData?.session_id || activeSessionId
        persistWebviewToken(activeToken, effectiveSessionId)

        if (initData?.ekyc_sdk_url && typeof sessionStorage !== 'undefined') {
          sessionStorage.setItem('dev_ekyc_sdk_url', initData.ekyc_sdk_url)
        }
        if (initData?.ekyc_sdk_token && typeof sessionStorage !== 'undefined') {
          sessionStorage.setItem('dev_ekyc_sdk_token', initData.ekyc_sdk_token)
          sessionStorage.setItem(`dev_ekyc_sdk_token_${effectiveSessionId}`, initData.ekyc_sdk_token)
        }
        if (initData?.ekyc_sdk_token_name && typeof sessionStorage !== 'undefined') {
          sessionStorage.setItem('dev_ekyc_sdk_token_name', initData.ekyc_sdk_token_name)
          sessionStorage.setItem(`dev_ekyc_sdk_token_name_${effectiveSessionId}`, initData.ekyc_sdk_token_name)
        }
        if (initData?.ekyc_sdk_access_token && typeof sessionStorage !== 'undefined') {
          sessionStorage.setItem('dev_ekyc_sdk_access_token', initData.ekyc_sdk_access_token)
          sessionStorage.setItem('dev_ekyc_access_token', initData.ekyc_sdk_access_token)
          sessionStorage.setItem(`dev_ekyc_sdk_access_token_${effectiveSessionId}`, initData.ekyc_sdk_access_token)
        }
        if (initData?.ekyc_sdk_resource_id && typeof sessionStorage !== 'undefined') {
          sessionStorage.setItem('dev_ekyc_sdk_resource_id', initData.ekyc_sdk_resource_id)
          sessionStorage.setItem(`dev_ekyc_sdk_resource_id_${effectiveSessionId}`, initData.ekyc_sdk_resource_id)
        }

        const branding = brandingFromTestSession(initData)
        persistPartnerBranding(effectiveSessionId, branding)

        const rawIdentity = initData?.identity_info
        const identity: Record<string, any> =
          rawIdentity && !Array.isArray(rawIdentity) && typeof rawIdentity === 'object'
            ? rawIdentity
            : {}

        const rawBank = initData?.bank_info
        const bank: Record<string, any> =
          rawBank && !Array.isArray(rawBank) && typeof rawBank === 'object'
            ? rawBank
            : {}

        const isRealSession = effectiveSessionId !== DEMO_SESSION_ID

        if (typeof sessionStorage !== 'undefined') {
          const prevDataStr =
            sessionStorage.getItem(`dev_identity_data_${effectiveSessionId}`) ||
            (!isRealSession ? sessionStorage.getItem('dev_identity_data') : null)
          let prevData = {}
          if (prevDataStr) {
            try {
              prevData = JSON.parse(prevDataStr)
            } catch {}
          }
          const merged = {
            ...prevData,
            ...identity,
            ...(bank.account_number ? { account_number: bank.account_number, bank_account: bank.account_number } : {}),
            ...(bank.bank_name ? { bank_name: bank.bank_name, bank: bank.bank_name } : {}),
            ...(bank.account_holder_name ? { account_name: bank.account_holder_name, bank_account_name: bank.account_holder_name } : {}),
            ...(bank.bank_branch ? { bank_branch: bank.bank_branch, branch: bank.bank_branch } : {}),
            ...(urlEmail ? { email: urlEmail } : {}),
            ...(urlFullName ? { full_name: urlFullName } : {}),
            ...(urlPhone ? { phone: urlPhone, phone_number: urlPhone } : {}),
            ...(urlAddress ? { address: urlAddress, permanent_address: urlAddress } : {}),
            ...(urlDob ? { date_of_birth: urlDob, dob: urlDob } : {}),
            ...(urlIdentityCard
              ? { identity_card: urlIdentityCard, id_no: urlIdentityCard, id_number: urlIdentityCard, tax_code: urlIdentityCard }
              : {}),
            ...(urlBankName ? { bank_name: urlBankName, bank: urlBankName } : {}),
            ...(urlAccountNumber ? { account_number: urlAccountNumber, bank_account: urlAccountNumber } : {}),
            ...(urlAccountName ? { account_name: urlAccountName, bank_account_name: urlAccountName } : {}),
            ...(urlBankBranch ? { bank_branch: urlBankBranch, branch: urlBankBranch } : {}),
          }
          if (!isRealSession) {
            sessionStorage.setItem('dev_identity_data', JSON.stringify(merged))
          }
          sessionStorage.setItem(`dev_identity_data_${effectiveSessionId}`, JSON.stringify(merged))
        }

        const demoContext = demoSessionContext(pathname, effectiveSessionId)
        const branded = applyPartnerBranding(demoContext, branding, {
          ...(urlPrimary ? { brand_color: urlPrimary } : {}),
          ...(urlSecondary ? { secondary_color: urlSecondary } : {}),
          ...(urlOnPrimary ? { on_primary: urlOnPrimary } : {}),
        })
        applyTheme(branded.theme)

        setContext({
          ...branded,
          session: {
            ...branded.session,
            id: effectiveSessionId,
            status: initData?.status === 'signed' ? 'signed' : 'pending',
            current_step: 'info',
          },
          subject: {
            ...branded.subject,
            full_name: identity.full_name || urlFullName || (isRealSession ? '' : branded.subject.full_name),
            email: identity.email || urlEmail || (isRealSession ? '' : branded.subject.email),
            phone: identity.phone || identity.phone_number || urlPhone || (isRealSession ? '' : branded.subject.phone),
            address: identity.address || urlAddress || (isRealSession ? '' : branded.subject.address),
            date_of_birth: identity.date_of_birth || urlDob || (isRealSession ? '' : branded.subject.date_of_birth),
            id_no: identity.id_number || identity.identity_card || urlIdentityCard || (isRealSession ? '' : branded.subject.id_no),
            bank_name: bank.bank_name || urlBankName || (branded.subject as any).bank_name || '',
            bank_account: bank.account_number || urlAccountNumber || (branded.subject as any).bank_account || '',
            account_name: bank.account_holder_name || urlAccountName || (branded.subject as any).account_name || identity.full_name || urlFullName || '',
            bank_branch: bank.bank_branch || urlBankBranch || (branded.subject as any).bank_branch || '',
          },
        })
      })
      .catch((err: unknown) => {
        console.error('initWebview error:', err)
        const errCode = (err as any)?.code
        const errMessage = String((err as any)?.message || '')
        const isSessionCompleted =
          errCode === 'SESSION_ERROR' ||
          errMessage.includes('Trạng thái session không cho phép') ||
          errMessage.includes('session không cho phép')
        const isSignedRoute = stepFromLocation(pathname) === 'signed'

        if (isSessionCompleted || isSignedRoute) {
          const fallbackContext = demoSessionContext(pathname, activeSessionId)
          const branding = loadPartnerBranding(activeSessionId)
          const branded = applyPartnerBranding(fallbackContext, branding, {
            ...(urlPrimary ? { brand_color: urlPrimary } : {}),
            ...(urlSecondary ? { secondary_color: urlSecondary } : {}),
            ...(urlOnPrimary ? { on_primary: urlOnPrimary } : {}),
          })
          applyTheme(branded.theme)
          setContext({
            ...branded,
            session: {
              ...branded.session,
              id: activeSessionId,
              status: 'signed',
            },
          })
          return
        }

        const message =
          errMessage ||
          'Không thể xác thực phiên Webview với hệ thống.'
        setError(message)
      })
  }, [attempt, urlSessionId, tokenFromUrl, urlPrimary, urlSecondary, urlOnPrimary, urlUserId, urlEmail, urlFullName, urlPhone, urlAddress, urlDob, urlIdentityCard, urlBankName, urlAccountNumber, urlAccountName, urlBankBranch])

  if (error) {
    return (
      <main className="startup" role="alert">
        <div className="startup__panel">
          <h1>Không mở được phiên ký</h1>
          <p>{error}</p>
          <div style={{ display: 'flex', gap: '12px', marginTop: '20px', justifyContent: 'center' }}>
            <button type="button" onClick={retry}>
              Thử lại
            </button>
            <button
              type="button"
              onClick={handleExit}
              style={{
                backgroundColor: 'transparent',
                border: '1px solid currentColor',
                color: 'inherit',
                cursor: 'pointer',
              }}
            >
              Đóng
            </button>
          </div>
        </div>
      </main>
    )
  }

  if (!context) {
    return (
      <main className="startup" aria-busy="true" aria-label="Đang tải phiên ký">
        <div className="startup__panel startup__panel--loading">
          <div className="startup__line startup__line--title" />
          <div className="startup__line" />
          <div className="startup__line startup__line--short" />
        </div>
      </main>
    )
  }

  return <App context={context} />
}

applyTheme(DEMO_SESSION_CONTEXT.theme)
createRoot(document.getElementById('root')!).render(<Bootstrap />)
